import React, { useState, useEffect } from 'react';
import { api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { ShieldAlert, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Wrapper per sezioni riservate (Hotel, Utenti, Notifiche Email).
 * Mostra un dialog di richiesta password OGNI volta che si apre la sezione.
 * Lo stato di sblocco vive in memoria React (non persiste tra ricariche o navigazioni).
 */
const SensitiveSectionGuard = ({ sectionName, children }) => {
  const [unlocked, setUnlocked] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get('/security/sensitive-password/status');
        if (mounted) setStatus(data);
      } catch (e) {
        if (mounted) setStatus({ is_set: false, can_manage: false, owner_email: '' });
      } finally {
        if (mounted) setLoadingStatus(false);
      }
    };
    fetchStatus();
    return () => { mounted = false; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Inserisci la password');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      await api.post('/security/sensitive-password/verify', { password });
      toast.success('Sezione sbloccata');
      setUnlocked(true);
      setPassword('');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Password errata';
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingStatus) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="sensitive-guard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  if (unlocked) return children;

  // Password non ancora impostata da Andrea
  if (!status?.is_set) {
    return (
      <div className="max-w-xl mx-auto mt-12" data-testid="sensitive-guard-not-set">
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2 text-amber-900">
              <ShieldAlert className="w-5 h-5" />
              Sezione protetta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-amber-900">
            <p>
              L'accesso a <strong>{sectionName}</strong> è riservato. La password di sicurezza non è ancora stata impostata.
            </p>
            <p className="text-sm">
              Solo <span className="font-mono">{status?.owner_email || 'l\'amministratore designato'}</span> può impostarla
              dalla pagina <strong>Profilo &rarr; Password Sezioni Riservate</strong>.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto mt-12" data-testid="sensitive-guard-prompt">
      <Card className="border-slate-300">
        <CardHeader>
          <CardTitle className="text-lg font-bold flex items-center gap-2 text-slate-900">
            <Lock className="w-5 h-5 text-[#1e3a5f]" />
            Accesso riservato — {sectionName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-4">
            Questa sezione richiede la password di sicurezza per essere consultata.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sensitive-pwd">Password</Label>
              <div className="relative">
                <Input
                  id="sensitive-pwd"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  placeholder="Inserisci la password"
                  data-testid="sensitive-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <Alert variant="destructive" data-testid="sensitive-password-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1e3a5f] hover:bg-[#152c4a]"
              data-testid="sensitive-password-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              Sblocca sezione
            </Button>
          </form>
          <p className="text-xs text-slate-400 mt-4">
            La password viene richiesta ogni volta che apri questa sezione.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SensitiveSectionGuard;
